// WebSocket service for real-time chat
import { WebSocketMessage, ChatMessage, PingMessage } from '../types';
import logger from '@/utils/logger';

type MessageHandler = (message: WebSocketMessage) => void;
type ConnectionHandler = (connected: boolean) => void;
type ErrorHandler = (error: Event) => void;

class WebSocketService {
  private ws: WebSocket | null = null;
  private url: string;
  private userId: string | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectInterval = 3000;
  private pingInterval: number | null = null;
  private isManualClose = false;

  // Event handlers
  private messageHandlers: MessageHandler[] = [];
  private connectionHandlers: ConnectionHandler[] = [];
  private errorHandlers: ErrorHandler[] = [];

  constructor() {
    // Priority: window.__WS_URL__ (runtime) > VITE_WS_URL (build-time) > fallback
    const wsUrl = (window as any).__WS_URL__ || 
      import.meta.env.VITE_WS_URL || 
      (window.location.hostname.includes('run.app') 
        ? 'wss://backend-real-auth-344710332118.us-central1.run.app'
        : 'ws://localhost:8000');
    this.url = wsUrl.replace('http://', 'ws://').replace('https://', 'wss://');
    // Reduced logging - only log in development
    if (import.meta.env.DEV) {
      console.log('🔌 WebSocket service initialized:', this.url);
    }
  }

  // Connect to WebSocket
  connect(userId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      logger.debug(`🔌 React connect() called with userId: ${userId}`);
      logger.debug(`🔌 Current WebSocket state: ${this.ws?.readyState || 'null'}`);
      
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        console.log('🔌 WebSocket already connected');
        resolve();
        return;
      }

      if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
        console.log('🔌 WebSocket already connecting, waiting...');
        resolve();
        return;
      }

      // Clean up existing connection
      if (this.ws) {
        console.log('🔌 Cleaning up existing WebSocket connection');
        this.ws.close();
        this.ws = null;
      }

      this.userId = userId;
      this.isManualClose = false;
      const wsUrl = `${this.url}/ws/${userId}`;

      console.log(`🔌 Attempting WebSocket connection...`);
      console.log(`🔌 Full URL: ${wsUrl}`);
      console.log(`🔌 User ID: ${userId}`);
      console.log(`🔌 Base URL: ${this.url}`);
      console.log(`🔌 WebSocket state before creation: ${typeof WebSocket}`);

      try {
        console.log(`🔌 Creating new WebSocket instance...`);
        this.ws = new WebSocket(wsUrl);
        console.log(`🔌 WebSocket instance created successfully`);
        console.log(`🔌 WebSocket readyState: ${this.ws.readyState}`);

        this.ws.onopen = (_) => {
          console.log('✅ WebSocket connected');
          this.reconnectAttempts = 0;
          this.startPingInterval();
          this.notifyConnectionHandlers(true);
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const message: WebSocketMessage = JSON.parse(event.data);
            // Only log important message types to reduce console spam
            if (['error', 'file', 'csv_file_created', 'csv_file_ready', 'image_file'].includes(message.type)) {
              console.log('📨 WebSocket:', message.type, message);
            }
            this.notifyMessageHandlers(message);
          } catch (error) {
            console.error('❌ Failed to parse WebSocket message:', error);
            console.error('❌ Raw data was:', event.data);
          }
        };

        this.ws.onclose = (event) => {
          console.log(`🔌 WebSocket closed: ${event.code} ${event.reason}`);
          this.stopPingInterval();
          this.notifyConnectionHandlers(false);

          // Attempt to reconnect if not manually closed
          if (!this.isManualClose && this.shouldReconnect()) {
            this.attemptReconnect();
          }
        };

        this.ws.onerror = (error) => {
          console.error('❌ WebSocket error occurred:', error);
          console.error('❌ Error type:', error.type);
          console.error('❌ Error target:', error.target);
          console.error('❌ WebSocket readyState during error:', this.ws?.readyState);
          console.error('❌ WebSocket URL during error:', wsUrl);
          this.notifyErrorHandlers(error);
          reject(error);
        };

      } catch (error) {
        const err = error as Error;
        console.error('❌ Exception thrown during WebSocket creation:', error);
        console.error('❌ Exception message:', err.message);
        console.error('❌ Exception stack:', err.stack);
        reject(error);
      }
    });
  }

  // Disconnect from WebSocket
  disconnect(): void {
    this.isManualClose = true;
    this.stopPingInterval();

    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      console.log('🔌 Manually closing WebSocket connection');
      this.ws.close(1000, 'Manual disconnect');
    } else if (this.ws) {
      console.log('🔌 Force closing WebSocket (state was not OPEN)');
      this.ws.close();
    }
    this.ws = null;
  }

  // Send chat message. Throws if WebSocket is not connected (caller should catch and show error)
  sendChatMessage(conversationId: string, content: string): void {
    console.log(`📤 sendChatMessage called - conversation: ${conversationId}, content: "${content}"`);
    console.log(`📤 WebSocket readyState: ${this.ws?.readyState || 'null'}`);
    
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      const errorMsg = 'Connexion perdue. Rechargez la page ou attendez la reconnexion avant d\'envoyer un message.';
      console.error(`❌ WebSocket not connected - cannot send message. readyState: ${this.ws?.readyState || 'null'}`);
      throw new Error(errorMsg);
    }
    
    const message: ChatMessage = {
      type: 'chat',
      conversation_id: conversationId,
      content: content.trim(),
    };

    console.log(`📤 Message object created:`, message);
    this.sendMessage(message);
  }

  // Send ping message
  sendPing(): void {
    const message: PingMessage = {
      type: 'ping',
    };

    this.sendMessage(message);
  }

  // Generic send message
  private sendMessage(message: any): void {
    // Reduce logging for ping messages (sent frequently)
    const isPing = message.type === 'ping';
    
    if (!isPing) {
      console.log(`📤 sendMessage called with:`, message);
    }
    
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      if (!isPing) {
        console.error(`❌ WebSocket is not connected - readyState: ${this.ws?.readyState || 'null'}`);
      }
      return;
    }

    try {
      const messageStr = JSON.stringify(message);
      if (!isPing) {
        console.log(`📤 Sending JSON string:`, messageStr);
      }
      this.ws.send(messageStr);
      if (!isPing) {
        console.log('✅ WebSocket message sent successfully:', message.type);
      }
    } catch (error) {
      const err = error as Error;
      console.error('❌ Failed to send WebSocket message:', error);
      console.error('❌ Error details:', err.message);
    }
  }

  // Check if should attempt reconnect
  private shouldReconnect(): boolean {
    return this.reconnectAttempts < this.maxReconnectAttempts;
  }

  // Attempt to reconnect
  private attemptReconnect(): void {
    if (!this.userId) {
      console.log('❌ Cannot reconnect: no user ID');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectInterval * this.reconnectAttempts;

    console.log(`🔄 Attempting to reconnect in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      if (!this.isManualClose && this.userId) {
        this.connect(this.userId).catch((error) => {
          console.error('❌ Reconnection failed:', error);
        });
      }
    }, delay);
  }

  // Start ping interval to keep connection alive
  private startPingInterval(): void {
    this.stopPingInterval();
    this.pingInterval = window.setInterval(() => {
      this.sendPing();
    }, 30000); // Ping every 30 seconds
  }

  // Stop ping interval
  private stopPingInterval(): void {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  // Event handler management
  onMessage(handler: MessageHandler): () => void {
    this.messageHandlers.push(handler);
    return () => {
      const index = this.messageHandlers.indexOf(handler);
      if (index > -1) {
        this.messageHandlers.splice(index, 1);
      }
    };
  }

  onConnection(handler: ConnectionHandler): () => void {
    this.connectionHandlers.push(handler);
    return () => {
      const index = this.connectionHandlers.indexOf(handler);
      if (index > -1) {
        this.connectionHandlers.splice(index, 1);
      }
    };
  }

  onError(handler: ErrorHandler): () => void {
    this.errorHandlers.push(handler);
    return () => {
      const index = this.errorHandlers.indexOf(handler);
      if (index > -1) {
        this.errorHandlers.splice(index, 1);
      }
    };
  }

  // Notify handlers
  private notifyMessageHandlers(message: WebSocketMessage): void {
    this.messageHandlers.forEach(handler => {
      try {
        handler(message);
      } catch (error) {
        console.error('❌ Error in message handler:', error);
      }
    });
  }

  private notifyConnectionHandlers(connected: boolean): void {
    this.connectionHandlers.forEach(handler => {
      try {
        handler(connected);
      } catch (error) {
        console.error('❌ Error in connection handler:', error);
      }
    });
  }

  private notifyErrorHandlers(error: Event): void {
    this.errorHandlers.forEach(handler => {
      try {
        handler(error);
      } catch (error) {
        console.error('❌ Error in error handler:', error);
      }
    });
  }

  // Get connection status
  isConnected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }

  // Get connection state
  getConnectionState(): string {
    if (!this.ws) return 'CLOSED';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'CONNECTING';
      case WebSocket.OPEN:
        return 'OPEN';
      case WebSocket.CLOSING:
        return 'CLOSING';
      case WebSocket.CLOSED:
        return 'CLOSED';
      default:
        return 'UNKNOWN';
    }
  }
}

// Export singleton instance
export const webSocketService = new WebSocketService();
export default webSocketService;
